<script lang="ts">
import {RouteNames} from "../../../../utils/constants.ts";

export function getRouteXAccountsFiatTopUp() {
  return {
    name: RouteNames.X_FIAT_TOPUP,
  };
}
</script>
<script setup lang="ts">
import BreadCrumbs from "../../../../components/BreadCrumbs.vue";
import BasicCard from "../../../../components/BasicCard.vue";
import TextInput from "../../../../components/form/TextInput.vue";
import InputSelect from "../../../../components/form/InputSelect.vue";
import InputCheckbox from "../../../../components/form/InputCheckbox.vue";
import { ref, computed } from "vue";
import { 
  ArrowRight as ArrowRightIcon,
  Plus as PlusIcon,
  CreditCard as CreditCardIcon,
  Check as CheckIcon,
  AlertTriangle as AlertTriangleIcon,
  Building as BuildingIcon,
  DollarSign as DollarSignIcon,
  Shield as ShieldIcon,
  Clock as ClockIcon
} from "lucide-vue-next";

// Target accounts for top-up
const targetAccounts = {
  "checking_usd": "Checking Account (USD) - $5,420.50",
  "savings_usd": "Savings Account (USD) - $12,340.00",
  "checking_eur": "Checking Account (EUR) - €2,150.75",
  "business_usd": "Business Account (USD) - $25,680.00"
};

// Payment methods
const paymentMethods = {
  "bank_transfer": "Bank Transfer (ACH) - Free, 2-3 days",
  "wire_transfer": "Wire Transfer - $15 fee, Same day",
  "debit_card": "Debit Card - $2.99 fee, Instant",
  "credit_card": "Credit Card - 3.5% fee, Instant",
  "external_wallet": "External Crypto Wallet - Network fees apply"
};

// Currencies
const currencies = {
  "USD": "US Dollar (USD)",
  "EUR": "Euro (EUR)",
  "GBP": "British Pound (GBP)",
  "CAD": "Canadian Dollar (CAD)"
};

// Form state
const selectedAccount = ref("checking_usd");
const paymentMethod = ref("bank_transfer");
const amount = ref("");
const currency = ref("USD");
const savePaymentMethod = ref(false);

// Payment method details
const bankDetails = ref({
  bankName: "",
  accountHolderName: "",
  accountNumber: "",
  routingNumber: "",
  accountType: "checking"
});

const cardDetails = ref({
  cardNumber: "",
  expiryDate: "",
  cvv: "",
  cardholderName: "",
  zipCode: ""
});

const cryptoDetails = ref({
  walletAddress: "",
  network: "ERC20",
  currency: "USDT"
});

// UI state
const step = ref(1); // 1: amount & method, 2: payment details, 3: review, 4: confirmation
const isLoading = ref(false);
const transactionId = ref("");

// Bank account types
const accountTypes = {
  "checking": "Checking Account",
  "savings": "Savings Account"
};

// Crypto networks
const cryptoNetworks = {
  "ERC20": "Ethereum (ERC-20)",
  "TRC20": "Tron (TRC-20)", 
  "BEP20": "Binance Smart Chain (BEP-20)",
  "SOL": "Solana",
  "MATIC": "Polygon"
};

// Stablecoin options
const stablecoins = {
  "USDT": "Tether (USDT)",
  "USDC": "USD Coin (USDC)",
  "DAI": "DAI Stablecoin",
  "BUSD": "Binance USD (BUSD)"
};

// Computed values
const processingFee = computed(() => {
  const amountNum = parseFloat(amount.value) || 0;
  const fees = {
    "bank_transfer": 0,
    "wire_transfer": 15,
    "debit_card": 2.99,
    "credit_card": amountNum * 0.035,
    "external_wallet": 5 // Network fee estimate
  };
  return fees[paymentMethod.value as keyof typeof fees] || 0;
});

const totalAmount = computed(() => {
  const amountNum = parseFloat(amount.value) || 0;
  return amountNum + processingFee.value;
});

const deliveryTime = computed(() => {
  const times = {
    "bank_transfer": "2-3 business days",
    "wire_transfer": "Same business day",
    "debit_card": "Instant",
    "credit_card": "Instant", 
    "external_wallet": "10-30 minutes"
  };
  return times[paymentMethod.value as keyof typeof times] || "";
});

const isCardPayment = computed(() => {
  return paymentMethod.value === "debit_card" || paymentMethod.value === "credit_card";
});

const isBankTransfer = computed(() => {
  return paymentMethod.value === "bank_transfer" || paymentMethod.value === "wire_transfer";
});

const isCryptoPayment = computed(() => {
  return paymentMethod.value === "external_wallet";
});

// Validation
const validateStep1 = () => {
  const amountNum = parseFloat(amount.value);
  return !isNaN(amountNum) && amountNum >= 10 && amountNum <= 50000 && selectedAccount.value && paymentMethod.value;
};

const validateStep2 = () => {
  if (isCardPayment.value) {
    return cardDetails.value.cardNumber && cardDetails.value.expiryDate && 
           cardDetails.value.cvv && cardDetails.value.cardholderName;
  } else if (isBankTransfer.value) {
    return bankDetails.value.bankName && bankDetails.value.accountHolderName &&
           bankDetails.value.accountNumber && bankDetails.value.routingNumber;
  } else if (isCryptoPayment.value) {
    return cryptoDetails.value.walletAddress && cryptoDetails.value.network && cryptoDetails.value.currency;
  }
  return true;
};

// Form handlers
const setQuickAmount = (quickAmount: number) => {
  amount.value = quickAmount.toString();
};

const nextStep = () => {
  if (step.value === 1 && validateStep1()) {
    step.value = 2;
  } else if (step.value === 2 && validateStep2()) {
    step.value = 3;
  }
};

const previousStep = () => {
  if (step.value > 1) {
    step.value--;
  }
};

const confirmTopUp = async () => {
  isLoading.value = true;
  
  // Simulate API call
  setTimeout(() => {
    transactionId.value = "TOP" + Math.random().toString(36).substring(2, 11).toUpperCase();
    step.value = 4;
    isLoading.value = false;
  }, 3000);
};

const startNewTopUp = () => {
  step.value = 1;
  amount.value = "";
  transactionId.value = "";
  // Reset payment details
  bankDetails.value = {
    bankName: "",
    accountHolderName: "",
    accountNumber: "",
    routingNumber: "",
    accountType: "checking"
  };
  cardDetails.value = {
    cardNumber: "",
    expiryDate: "",
    cvv: "",
    cardholderName: "",
    zipCode: ""
  };
  cryptoDetails.value = {
    walletAddress: "",
    network: "ERC20",
    currency: "USDT"
  };
};

// Top-up benefits
const topUpBenefits = [
  "Instant access to funds",
  "Secure encrypted transactions",
  "Multiple funding sources",
  "Real-time transaction tracking",
  "24/7 customer support"
];
</script>

<template>
  <BreadCrumbs :title="$t('Top Up Balance')" class="mb-4 md:mb-5 px-4 md:px-0" />

  <div class="max-w-2xl mx-auto px-4 md:px-0">
    <!-- Step Indicator -->
    <div class="flex justify-center mb-6 md:mb-8">
      <div class="flex items-center space-x-2 md:space-x-4">
        <div class="flex items-center">
          <div :class="[step >= 1 ? 'bg-blue-500 text-white' : 'bg-gray-300 text-gray-600', 'w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center text-xs md:text-sm font-medium']">
            1
          </div>
          <span class="ml-1 md:ml-2 text-xs md:text-sm hidden sm:inline">Amount & Method</span>
        </div>
        <arrow-right-icon class="h-3 w-3 md:h-4 md:w-4 text-gray-400" />
        <div class="flex items-center">
          <div :class="[step >= 2 ? 'bg-blue-500 text-white' : 'bg-gray-300 text-gray-600', 'w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center text-xs md:text-sm font-medium']">
            2
          </div>
          <span class="ml-1 md:ml-2 text-xs md:text-sm hidden sm:inline">Payment Details</span>
        </div>
        <arrow-right-icon class="h-3 w-3 md:h-4 md:w-4 text-gray-400" />
        <div class="flex items-center">
          <div :class="[step >= 3 ? 'bg-blue-500 text-white' : 'bg-gray-300 text-gray-600', 'w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center text-xs md:text-sm font-medium']">
            3
          </div>
          <span class="ml-1 md:ml-2 text-xs md:text-sm hidden sm:inline">Review</span>
        </div>
        <arrow-right-icon class="h-3 w-3 md:h-4 md:w-4 text-gray-400" />
        <div class="flex items-center">
          <div :class="[step >= 4 ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600', 'w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center text-xs md:text-sm font-medium']">
            <check-icon v-if="step >= 4" class="h-3 w-3 md:h-4 md:w-4" />
            <span v-else>4</span>
          </div>
          <span class="ml-1 md:ml-2 text-xs md:text-sm hidden sm:inline">Complete</span>
        </div>
      </div>
    </div>

    <!-- Step 1: Amount & Method -->
    <div v-if="step === 1" class="space-y-4 md:space-y-6">
      <!-- Benefits Banner -->
      <BasicCard>
        <div class="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
          <plus-icon class="h-5 w-5 md:h-6 md:w-6 text-green-600" />
          <h2 class="text-lg md:text-xl font-semibold">Top Up Your Account</h2>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <div>
            <h3 class="font-medium mb-2 md:mb-3 text-sm md:text-base">Why top up?</h3>
            <ul class="space-y-1 md:space-y-2">
              <li v-for="benefit in topUpBenefits" :key="benefit" class="flex items-center gap-2 text-xs md:text-sm">
                <check-icon class="h-3 w-3 md:h-4 md:w-4 text-green-600 flex-shrink-0" />
                {{ benefit }}
              </li>
            </ul>
          </div>
          <div class="bg-blue-50 p-3 md:p-4 rounded-lg">
            <div class="flex items-center gap-2 mb-2">
              <shield-icon class="h-4 w-4 md:h-5 md:w-5 text-blue-600" />
              <span class="font-medium text-blue-800 text-sm md:text-base">Secure & Reliable</span>
            </div>
            <p class="text-xs md:text-sm text-blue-700">
              All transactions are encrypted and monitored 24/7 for your security.
            </p>
          </div>
        </div>
      </BasicCard>

      <!-- Top-up Form -->
      <BasicCard>
        <form @submit.prevent="nextStep" class="space-y-4 md:space-y-6">
          <!-- Target Account -->
          <div>
            <label class="text-xs md:text-sm text-gray-600 mb-2 block">Top up account</label>
            <InputSelect
              v-model="selectedAccount"
              :list="targetAccounts"
              placeholder="Select account to top up"
            />
          </div>

          <!-- Amount -->
          <div>
            <label class="text-xs md:text-sm text-gray-600 mb-2 block">Amount to top up</label>
            <div class="flex flex-wrap gap-2 mb-3">
              <button
                v-for="quickAmount in [100, 500, 1000, 5000]"
                :key="quickAmount"
                type="button"
                @click="setQuickAmount(quickAmount)"
                class="px-2 md:px-3 py-1.5 md:py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-xs md:text-sm"
              >
                ${{ quickAmount }}
              </button>
            </div>
            <div class="flex gap-2 md:gap-3">
              <div class="flex-1">
                <TextInput
                  v-model="amount"
                  name="amount"
                  type="number"
                  placeholder="0.00"
                  step="0.01"
                />
              </div>
              <div class="w-20 md:w-24">
                <InputSelect
                  v-model="currency"
                  :list="currencies"
                />
              </div>
            </div>
            <p class="text-xs text-gray-500 mt-1">
              Minimum: $10 • Maximum: $50,000 per transaction
            </p>
          </div>

          <!-- Payment Method -->
          <div>
            <label class="text-xs md:text-sm text-gray-600 mb-2 block">Payment method</label>
            <div class="space-y-2 md:space-y-3">
              <div
                v-for="(description, method) in paymentMethods"
                :key="method"
                @click="paymentMethod = method"
                :class="[
                  'p-3 md:p-4 border rounded-lg cursor-pointer transition-all',
                  paymentMethod === method ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                ]"
              >
                <div class="flex justify-between items-center gap-2">
                  <div class="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
                    <building-icon v-if="method.includes('bank') || method.includes('wire')" class="h-4 w-4 md:h-5 md:w-5 text-gray-600 flex-shrink-0" />
                    <credit-card-icon v-else-if="method.includes('card')" class="h-4 w-4 md:h-5 md:w-5 text-gray-600 flex-shrink-0" />
                    <dollar-sign-icon v-else class="h-4 w-4 md:h-5 md:w-5 text-gray-600 flex-shrink-0" />
                    <div class="min-w-0 flex-1">
                      <p class="font-medium text-xs md:text-sm">{{ method.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) }}</p>
                      <p class="text-xs md:text-sm text-gray-600 truncate">{{ description }}</p>
                    </div>
                  </div>
                  <div v-if="paymentMethod === method" class="text-blue-500 flex-shrink-0">
                    <check-icon class="h-4 w-4 md:h-5 md:w-5" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Fee Preview -->
          <div v-if="amount" class="bg-gray-50 p-3 md:p-4 rounded-lg">
            <div class="flex justify-between text-xs md:text-sm mb-2">
              <span>Top-up Amount</span>
              <span>${{ parseFloat(amount).toFixed(2) }}</span>
            </div>
            <div class="flex justify-between text-xs md:text-sm mb-2">
              <span>Processing Fee</span>
              <span>${{ processingFee.toFixed(2) }}</span>
            </div>
            <div class="border-t pt-2 flex justify-between font-medium text-sm md:text-base">
              <span>Total Cost</span>
              <span>${{ totalAmount.toFixed(2) }}</span>
            </div>
            <div class="flex items-center gap-2 mt-2 text-xs md:text-sm text-gray-600">
              <clock-icon class="h-3 w-3 md:h-4 md:w-4" />
              <span>Estimated delivery: {{ deliveryTime }}</span>
            </div>
          </div>

          <!-- Validation Messages -->
          <div v-if="amount && parseFloat(amount) < 10" class="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <alert-triangle-icon class="h-4 w-4 md:h-5 md:w-5 text-yellow-600 flex-shrink-0" />
            <span class="text-xs md:text-sm text-yellow-800">Minimum top-up amount is $10</span>
          </div>

          <div v-if="amount && parseFloat(amount) > 50000" class="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <alert-triangle-icon class="h-4 w-4 md:h-5 md:w-5 text-red-600 flex-shrink-0" />
            <span class="text-xs md:text-sm text-red-800">Maximum top-up amount is $50,000</span>
          </div>

          <!-- Action Button -->
          <div class="flex justify-end pt-3 md:pt-4">
            <button
              type="submit"
              :disabled="!validateStep1()"
              class="w-full sm:w-auto px-6 md:px-8 py-2.5 md:py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm md:text-base"
            >
              Continue
            </button>
          </div>
        </form>
      </BasicCard>
    </div>

    <!-- Step 2: Payment Details -->
    <BasicCard v-if="step === 2">
      <div class="space-y-4 md:space-y-6">
        <div class="flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
          <credit-card-icon class="h-5 w-5 md:h-6 md:w-6 text-purple-600" />
          <h2 class="text-lg md:text-xl font-semibold">Payment Details</h2>
        </div>

        <form @submit.prevent="nextStep" class="space-y-4 md:space-y-6">
          <!-- Bank Transfer Details -->
          <div v-if="isBankTransfer" class="space-y-3 md:space-y-4">
            <h3 class="font-medium text-sm md:text-base">Bank Account Information</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              <TextInput
                v-model="bankDetails.bankName"
                name="bankName"
                type="text"
                label="Bank Name"
                placeholder="Chase Bank"
              />
              <TextInput
                v-model="bankDetails.accountHolderName"
                name="accountHolderName"
                type="text"
                label="Account Holder Name"
                placeholder="John Doe"
              />
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              <TextInput
                v-model="bankDetails.routingNumber"
                name="routingNumber"
                type="text"
                label="Routing Number"
                placeholder="123456789"
              />
              <TextInput
                v-model="bankDetails.accountNumber"
                name="accountNumber"
                type="text"
                label="Account Number"
                placeholder="1234567890"
              />
            </div>
            <div class="w-full md:w-1/2">
              <label class="text-xs md:text-sm text-gray-600 mb-2 block">Account Type</label>
              <InputSelect
                v-model="bankDetails.accountType"
                :list="accountTypes"
              />
            </div>
          </div>

          <!-- Card Details -->
          <div v-if="isCardPayment" class="space-y-3 md:space-y-4">
            <h3 class="font-medium text-sm md:text-base">Card Information</h3>
            <TextInput
              v-model="cardDetails.cardNumber"
              name="cardNumber"
              type="text"
              label="Card Number"
              placeholder="1234 5678 9012 3456"
              maxlength="19"
            />
            <div class="grid grid-cols-3 gap-2 md:gap-4">
              <TextInput
                v-model="cardDetails.expiryDate"
                name="expiryDate"
                type="text"
                label="Expiry Date"
                placeholder="MM/YY"
                maxlength="5"
              />
              <TextInput
                v-model="cardDetails.cvv"
                name="cvv"
                type="text"
                label="CVV"
                placeholder="123"
                maxlength="4"
              />
              <TextInput
                v-model="cardDetails.zipCode"
                name="zipCode"
                type="text"
                label="ZIP Code"
                placeholder="12345"
              />
            </div>
            <TextInput
              v-model="cardDetails.cardholderName"
              name="cardholderName"
              type="text"
              label="Cardholder Name"
              placeholder="John Doe"
            />
          </div>

          <!-- Crypto Wallet Details -->
          <div v-if="isCryptoPayment" class="space-y-3 md:space-y-4">
            <h3 class="font-medium text-sm md:text-base">Crypto Wallet Information</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              <div>
                <label class="text-xs md:text-sm text-gray-600 mb-2 block">Network</label>
                <InputSelect
                  v-model="cryptoDetails.network"
                  :list="cryptoNetworks"
                />
              </div>
              <div>
                <label class="text-xs md:text-sm text-gray-600 mb-2 block">Currency</label>
                <InputSelect
                  v-model="cryptoDetails.currency"
                  :list="stablecoins"
                />
              </div>
            </div>
            <TextInput
              v-model="cryptoDetails.walletAddress"
              name="walletAddress"
              type="text"
              label="Your Wallet Address"
              placeholder="0x742d35Cc6631C0532925a3b8D43A8Fb33A39d2af"
            />
            <div class="bg-blue-50 border border-blue-200 rounded-lg p-3 md:p-4">
              <p class="text-xs md:text-sm text-blue-800">
                <strong>Note:</strong> You'll send {{ cryptoDetails.currency }} from your wallet to our address.
                We'll process the conversion automatically.
              </p>
            </div>
          </div>

          <!-- Save Payment Method -->
          <div>
            <InputCheckbox v-model="savePaymentMethod" name="savePaymentMethod">
              <span class="text-xs md:text-sm">Save this payment method for future top-ups</span>
            </InputCheckbox>
          </div>

          <!-- Action Buttons -->
          <div class="flex flex-col-reverse sm:flex-row justify-between gap-3 pt-4 md:pt-6">
            <button
              type="button"
              @click="previousStep"
              class="w-full sm:w-auto px-4 md:px-6 py-2.5 md:py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm md:text-base"
            >
              Back
            </button>
            <button
              type="submit"
              :disabled="!validateStep2()"
              class="w-full sm:w-auto px-6 md:px-8 py-2.5 md:py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm md:text-base"
            >
              Continue
            </button>
          </div>
        </form>
      </div>
    </BasicCard>

    <!-- Step 3: Review -->
    <BasicCard v-if="step === 3">
      <div class="space-y-4 md:space-y-6">
        <div class="flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
          <alert-triangle-icon class="h-5 w-5 md:h-6 md:w-6 text-orange-600" />
          <h2 class="text-lg md:text-xl font-semibold">Review Top-up</h2>
        </div>

        <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-3 md:p-4 mb-4 md:mb-6">
          <div class="flex items-start gap-2 md:gap-3">
            <alert-triangle-icon class="h-4 w-4 md:h-5 md:w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 class="font-medium text-yellow-800 mb-1 text-sm md:text-base">Please Review Carefully</h3>
              <p class="text-xs md:text-sm text-yellow-700">
                Verify all details before confirming. Processing fees are non-refundable.
              </p>
            </div>
          </div>
        </div>

        <!-- Top-up Summary -->
        <div class="space-y-3 md:space-y-4">
          <div class="p-3 md:p-4 bg-gray-50 rounded-lg">
            <h3 class="font-medium text-gray-800 mb-3 text-sm md:text-base">Top-up Details</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 text-xs md:text-sm">
              <div>
                <span class="text-gray-600">To Account</span>
                <p class="font-medium break-words">{{ targetAccounts[selectedAccount as keyof typeof targetAccounts] }}</p>
              </div>
              <div>
                <span class="text-gray-600">Amount</span>
                <p class="font-medium">${{ amount }} {{ currency }}</p>
              </div>
              <div>
                <span class="text-gray-600">Payment Method</span>
                <p class="font-medium break-words">{{ paymentMethods[paymentMethod as keyof typeof paymentMethods] }}</p>
              </div>
              <div>
                <span class="text-gray-600">Delivery Time</span>
                <p class="font-medium">{{ deliveryTime }}</p>
              </div>
            </div>
          </div>

          <div class="p-3 md:p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div class="flex justify-between items-center">
              <span class="font-medium text-blue-800 text-sm md:text-base">Total Cost</span>
              <span class="font-semibold text-base md:text-lg text-blue-800">${{ totalAmount.toFixed(2) }}</span>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex flex-col-reverse sm:flex-row justify-between gap-3 pt-4 md:pt-6">
          <button
            @click="previousStep"
            class="w-full sm:w-auto px-4 md:px-6 py-2.5 md:py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm md:text-base"
          >
            Back
          </button>
          <button
            @click="confirmTopUp"
            :disabled="isLoading"
            class="w-full sm:w-auto px-6 md:px-8 py-2.5 md:py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm md:text-base"
          >
            <span v-if="isLoading">Processing...</span>
            <span v-else>Confirm Top-up</span>
          </button>
        </div>
      </div>
    </BasicCard>

    <!-- Step 4: Confirmation -->
    <BasicCard v-if="step === 4">
      <div class="text-center space-y-4 md:space-y-6 px-4 md:px-0">
        <div class="flex justify-center">
          <div class="w-12 h-12 md:w-16 md:h-16 bg-green-100 rounded-full flex items-center justify-center">
            <check-icon class="h-6 w-6 md:h-8 md:w-8 text-green-600" />
          </div>
        </div>

        <div>
          <h2 class="text-xl md:text-2xl font-semibold text-green-800 mb-2">Top-up Initiated!</h2>
          <p class="text-sm md:text-base text-gray-600">
            Your account top-up has been submitted successfully.
          </p>
        </div>

        <div class="bg-gray-50 p-3 md:p-4 rounded-lg">
          <p class="text-xs md:text-sm text-gray-600 mb-2">Transaction ID</p>
          <code class="text-xs md:text-sm font-mono bg-white p-2 rounded border block break-all">
            {{ transactionId }}
          </code>
        </div>

        <div class="space-y-1 md:space-y-2">
          <p class="font-medium text-sm md:text-base break-words">${{ amount }} top-up to {{ targetAccounts[selectedAccount as keyof typeof targetAccounts].split(' -')[0] }}</p>
          <p class="text-xs md:text-sm text-gray-600">Processing fee: ${{ processingFee.toFixed(2) }}</p>
          <p class="text-xs md:text-sm text-gray-500">Estimated arrival: {{ deliveryTime }}</p>
        </div>

        <div class="flex flex-col sm:flex-row justify-center gap-3 md:gap-4 pt-4 md:pt-6">
          <button
            @click="startNewTopUp"
            class="w-full sm:w-auto px-4 md:px-6 py-2.5 md:py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm md:text-base"
          >
            Top Up Again
          </button>
          <button
            @click="$router.push({ name: 'account.fiat' })"
            class="w-full sm:w-auto px-4 md:px-6 py-2.5 md:py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm md:text-base"
          >
            Back to Accounts
          </button>
        </div>
      </div>
    </BasicCard>
  </div>
</template>

<style scoped lang="scss">

</style>
